import { useCallback, useRef, useState } from 'react';
import { MoveHorizontal } from 'lucide-react';

// A classic drag-to-reveal before/after comparison. Percentages (not pixel
// measurements) drive the clip-path so it works at any container size.
export const BeforeAfterSlider = ({ before, after, caption, testId }) => {
  const containerRef = useRef(null);
  const [percent, setPercent] = useState(50);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPercent(Math.min(100, Math.max(0, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    updateFromClientX(e.clientX);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') setPercent((p) => Math.max(0, p - 5));
    if (e.key === 'ArrowRight') setPercent((p) => Math.min(100, p + 5));
  };

  return (
    <div data-testid={testId} className="w-full">
      <div
        ref={containerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        role="slider"
        tabIndex={0}
        aria-label="Before and after comparison slider"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        onKeyDown={onKeyDown}
        className="relative aspect-[4/3] w-full select-none overflow-hidden rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#141416] cursor-ew-resize touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lxra-gold))]"
      >
        {/* After — full image, bottom layer */}
        <img src={after.image_url} alt="After" className="absolute inset-0 h-full w-full object-cover pointer-events-none" draggable={false} />

        {/* Before — clipped to the current percentage */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ clipPath: `inset(0 ${100 - percent}% 0 0)` }}>
          <img src={before.image_url} alt="Before" className="h-full w-full object-cover" draggable={false} />
        </div>

        {/* Labels */}
        <span className="absolute top-3 left-3 rounded-full bg-black/60 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-white pointer-events-none">
          Before
        </span>
        <span className="absolute top-3 right-3 rounded-full bg-black/60 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-white pointer-events-none">
          After
        </span>

        {/* Drag handle */}
        <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${percent}%` }}>
          <div className="absolute inset-y-0 -translate-x-1/2 w-px bg-[hsl(var(--lxra-gold))]" />
          <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] flex items-center justify-center shadow-lg">
            <MoveHorizontal className="h-4 w-4" />
          </div>
        </div>
      </div>
      {caption && <p className="mt-2 text-xs text-[hsl(var(--lxra-muted))] text-center">{caption}</p>}
    </div>
  );
};
